import React, { useEffect, useMemo } from 'react'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import styled, { useTheme } from 'styled-components'
import { Flex, PopoverMenu, Button, Text, VerifiedIcon, TextVariant, SingleMenuItem, IconWrapper } from '@mprolab/uikit'
import { ArrowUpRight, ChevronDown } from 'react-feather'
import {
  accessTokenLocalStorageKey,
  ConnectorName,
  initialConnectorIdLocalStorageKey,
  initialWalletLoginLocalStorageKey,
  SessionType,
  shortenAddress,
  UserState
} from '@mprolab/sdk'
import { useConnectPanel } from 'contexts/ConnectPanel'
import { useIsUserVerified, useUserData } from 'state/user/hooks'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { useToggle } from 'hooks/useToggle'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useAwaitingVerificationModal, useAwaitingWalletVerificationModal } from 'components/widgets/VerificationModals'
import { useRouter } from 'hooks/useRouter'
import { indexNodeStore } from 'mobxStores/IndexNodeStore'
import { useVerifyIndexNode } from 'hooks/useVerifyIndexNode'
import { useUserVerificationState } from 'utils/useUserVerificationState'
import { useLifetimeTokens } from 'state/global/hooks'
import { AppDispatch } from 'state'
import { useDispatch } from 'react-redux'
import { setLifetimeTokens } from 'state/global/actions'
import { useVerify } from 'state/user/hooks/useVerifyUser'
import useToast from 'hooks/useToast'
import { useTranslation } from 'react-i18next'
import useAuth from 'hooks/useAuth'
import isNil from 'lodash/isNil'
import { logMessage } from 'utils/logMessage'
import { fetchWalletAccountCheck } from 'utils/walletAccountPrecheck'
import UserPanelButton from './Button/UserPanelButton'
import { buildUserMenu } from './userMenu'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'

const VerifiedWalletIcon = styled(VerifiedIcon)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

const StyledWrapper = styled(Flex)`
  cursor: pointer;
`

const WalletRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`

const WalletAddress = styled(Text)`
  color: ${({ theme }) => theme.colors.gray2};
  font-size: 14px;
  line-height: normal;
  font-weight: 600;
  ${({ theme }) => theme.mediaQueries.mobile} {
    font-size: 12px;
  }
`

const StyledChevron = styled(ChevronDown)<{ open: boolean }>`
  color: ${({ theme }) => theme.colors.gray2};
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease-in-out;
`

const ClickableWrapper = styled(Flex)`
  align-items: center;
  gap: 8px;
`

const UserPanel: React.FC = () => {
  const { t } = useTranslation()
  const { push, pathname, replace } = useRouter()
  const { colors } = useTheme()
  const { connectPanelDisabled, setConnectPanelOpen } = useConnectPanel()
  const isUserVerifiedRedux = useIsUserVerified()
  const { account, isActive, provider, isActivating, connector } = useWeb3React()
  const noAccount = !account && !isActivating
  const user = useUserData()
  const isUserVerified = !!(isUserVerifiedRedux && user?.userData?.userId)
  const { verificationState, setVerificationState } = useUserVerificationState()
  const isMobile = useThemeMediaQuery('mobile')
  const localAccessToken = localStorage.getItem(accessTokenLocalStorageKey)
  const initialConnectorId = localStorage.getItem(initialConnectorIdLocalStorageKey)
  const { logout } = useAuth()

  const { onPresentAwaitingVerificationModal, onModalClose } = useAwaitingWalletVerificationModal({})
  const dispatch = useDispatch<AppDispatch>()
  const lifetimeTokens = useLifetimeTokens()
  const { onPresentAwaitingVerificationModal: onPresentWeb3Modal, onModalClose: onWeb3ModalClose } =
    useAwaitingVerificationModal({})
  const { verifyUser } = useVerify()
  const [open, toggle] = useToggle(false)
  const verifyIndexNode = useVerifyIndexNode()
  const { toastError } = useToast()

  const handleCheckWallet = async (acct: string, link: string) => {
    if (acct) {
      const check = await fetchWalletAccountCheck(acct)

      if (check?.rulesCheckbox && check?.hasAccount) {
        setConnectPanelOpen(false)
        replace(
          {
            pathname: '/login',
            query: {
              rulesCheckboxChecked: true
            }
          },
          undefined,
          { shallow: true }
        )
      } else {
        onPresentWeb3Modal()
        await verifyUser()
        const initialWalletLogin = localStorage.getItem(initialWalletLoginLocalStorageKey)
        onWeb3ModalClose()
        setConnectPanelOpen(false)
        if (initialWalletLogin === 'true') {
          replace(
            {
              pathname: '/login'
            },
            undefined,
            { shallow: true }
          )
        } else {
          push(link)
        }
      }
    }
  }

  const ButtonView = useMemo(() => {
    if (isMobile) return <UserPanelButton />
    switch (true) {
      case verificationState === UserState.VERIFIED && user?.userData?.addresses?.length === 0:
        return <UserPanelButton title="Add wallet" />
      case verificationState === UserState.VERIFIED && !account:
        return <UserPanelButton title="Connect wallet" />
      default:
        return <UserPanelButton />
    }
  }, [verificationState, user?.userData?.addresses?.length, account, isMobile])

  const items = useMemo(
    () =>
      buildUserMenu({
        signedIn: Boolean(account || localAccessToken),
        logout: () => logout({ connector }),
        logoutIcon: <IconWrapper icon={<ArrowUpRight size={16} />} />
      }),
    [account, localAccessToken, connector, logout]
  )

  useEffect(() => {
    if ((!account || !isActive) && !isUserVerified) {
      setVerificationState(UserState.UNAUTHORIZED)
    } else if (account && !isUserVerified) {
      setVerificationState(UserState.AUTHORIZED)
    } else if (
      (account && isUserVerified) ||
      (!account && isUserVerified && !Object.values(ConnectorName).includes(initialConnectorId as ConnectorName))
    ) {
      setVerificationState(UserState.VERIFIED)
    } else {
      setVerificationState(UserState.UNAUTHORIZED)
    }
  }, [isUserVerified, account, isActive, localAccessToken, initialConnectorId, setVerificationState])

  if (pathname === '/login') {
    return <div style={{ height: '40px' }} />
  }

  if (verificationState === UserState.UNAUTHORIZED) {
    return (
      <Button
        size="sm"
        disabled={connectPanelDisabled}
        onClick={() => setConnectPanelOpen(true)}
        testId="headerComponent-connect-button"
      >
        <Text clickable variant={isMobile ? TextVariant.TEXT_XS_BOLD : TextVariant.BUTTON}>
          {t('general.login')}
        </Text>
      </Button>
    )
  }

  const obtainLifeTimeToken = async () => {
    indexNodeStore.setIsLoading(true)
    onPresentAwaitingVerificationModal()
    const response = await verifyIndexNode({
      account,
      provider,
      successCallback: async (accessToken) => {
        dispatch(setLifetimeTokens({ account, accessToken }))
        onModalClose()
      },
      sessionType: SessionType.LIFETIME
    })
    indexNodeStore.setIsLoading(false)

    if (typeof response === 'object' && response !== null && 'code' in response) {
      onModalClose()
      indexNodeStore.setIsLoading(false)
      throw response
    }
  }

  const handleVerification = async () => {
    onPresentAwaitingVerificationModal()
    await verifyUser({
      onSuccessCallback: () => {
        onModalClose()
      },
      onErrorCallback: () => {
        onModalClose()
        toastError(t('walletConnect.web3loginError'))
      }
    })
  }

  const handleWeb3Login = async (link: string) => {
    await handleCheckWallet(account!, link)
  }

  const handleError = (error: unknown) => {
    onModalClose()
    logMessage(error)
    // Same decoder as everywhere else: a signature declined, a wallet that
    // cannot switch network and a service that did not answer are three
    // different sentences, and the user can act on two of them.
    toastError(getErrorCodesDescription(t, error))
  }

  const handleMenuClick = async (item: SingleMenuItem) => {
    try {
      const storedToken = lifetimeTokens?.[account!]

      if (item.label === 'Logout') {
        item.onClick()
        return
      }

      if (item.tokenRestricted && !storedToken && !noAccount) {
        await obtainLifeTimeToken()
      }

      if (!isUserVerified && item.verificationRestricted) {
        await handleVerification()
      }

      if (!isUserVerified && item.web3Login) {
        await handleWeb3Login(item.link)
        return
      }

      push(item.link)
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <Flex gap="8px">
      <PopoverMenu
        {...{ menuItems: items }}
        onItemClick={(item) => handleMenuClick(item)}
        // Wide enough for the longest row plus both icons, so nothing wraps.
        minWidth="240px"
        anchorOrigin={{
          vertical: 60,
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <StyledWrapper>
          <ClickAwayListener
            onClickAway={() => {
              if (open) {
                toggle()
              }
            }}
          >
            <ClickableWrapper onClick={toggle}>
              <WalletRow>
                {!isNil(account) && (
                  <VerifiedWalletIcon
                    stroke={verificationState === UserState.VERIFIED ? colors.gray10 : colors.white}
                    fill={verificationState === UserState.VERIFIED ? colors.cyan : colors.white}
                  />
                )}
                {account && (
                  <WalletAddress
                    clickable
                    variant={isMobile ? TextVariant.TEXT_XS_BOLD : TextVariant.BODY_DEFAULT_BOLD}
                  >
                    {shortenAddress(account, 3, 5)}
                  </WalletAddress>
                )}
              </WalletRow>
              <StyledChevron open={open} />
            </ClickableWrapper>
          </ClickAwayListener>
        </StyledWrapper>
      </PopoverMenu>
      {ButtonView}
    </Flex>
  )
}

export default UserPanel
